import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import {
  ApprovalsMaterializedApprovalFlowHistoryDocument,
  InboxTaskListItemCompletionFragmentDoc,
  TaskActivityDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import useBulkApproveRejectFlows from 'modules/approvals/hooks/useBulkApproveRejectFlows'
import TaskFooter from 'modules/inbox_tasks/activities/Task/Footer'
import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'
import {
  TaskCustomComponent,
  TaskDefinition,
  TaskFooterComponent,
} from 'modules/inbox_tasks/lib/abstraction'

import { getAtsRequisitionMetadata } from './AtsRequisition'
import { getAttendanceEditTimesheetRequestMetadata } from './AttendanceEditTimesheetRequest'
import { getAttendanceOvertimeRequestMetadata } from './AttendanceOvertimeRequest'
import { getBankingCardMetadata } from './BankingCard'
import { getContractsContractVersionMetadata } from './ContractsContractVersion/metadata'
import { getContractsContractVersionRequestMetadata } from './ContractsContractVersionRequest/metadata'
import { getExpensesExpensableMetadata } from './ExpensesExpensable'
import { getProcurementPurchaseRequestMetadata } from './ProcurementPurchaseRequest'
import { getShiftManagementSwapShiftMetadata } from './ShiftManagementSwapShift'
import { getTimeoffLeaveMetadata } from './TimeoffLeave'
import { getTrainingsTrainingRequestMetadata } from './TrainingsTrainingRequest'
import { RESOURCE_COMPONENTS, RESOURCE_TITLES, getResourceTextFromResourceType } from './helpers'

type State = {
  additionalComments?: string
}

const ApprovalTaskContent: TaskCustomComponent<'ApprovalsMaterializedApprovalStep', State> = ({
  affectee,
  ...props
}) => {
  const taskable = 'taskable' in props ? props.taskable : undefined

  const materializedApprovalFlow = taskable?.materializedApprovalFlow

  const approvable = materializedApprovalFlow?.approvable
  const approvalFlowResourceId = materializedApprovalFlow?.resourceId

  const resourceId = useMemo(() => {
    if (approvable && 'id' in approvable) return approvable.id

    return approvalFlowResourceId
  }, [approvable, approvalFlowResourceId])

  const resourceType = materializedApprovalFlow?.resourceType ?? ''
  const reason = materializedApprovalFlow?.reason
  const isRejected = materializedApprovalFlow?.status === 'rejected'
  const resourceTypename = materializedApprovalFlow?.approvable?.__typename ?? ''

  const owner = materializedApprovalFlow?.owner?.employeeName

  const ownerId = owner?.id

  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })

  const Content = useMemo(() => {
    if (!resourceId || !resourceTypename) {
      return (
        <F0Alert
          title={i18n.t('common.approvals.no_permission.title')}
          description={i18n.t('common.approvals.no_permission.description')}
          variant="info"
        />
      )
    }

    const Component = RESOURCE_COMPONENTS[resourceTypename]

    if (!Component) return null

    return (
      <Component
        id={resourceId}
        requesterContent={requesterContent}
        affectee={affectee}
        requesterEmployeeId={ownerId}
      />
    )
  }, [affectee, resourceTypename, resourceId, requesterContent, ownerId])

  return (
    <Box gap="s24" overflowX="hidden" flexGrow>
      {Content}
      {resourceId && (
        <ApprovalsInformationBlock resourceId={resourceId} resourceType={resourceType} newLook />
      )}
      {isRejected && !!reason && (
        <NoteField label={i18n.t('settings.approvals.rejection_reason')} content={reason} />
      )}
    </Box>
  )
}

const ApprovalTaskFooter: TaskFooterComponent<'ApprovalsMaterializedApprovalStep', State> = ({
  taskId,
  taskKind,
  taskable,
  state,
}) => {
  const navigate = useNavigate()

  const { search } = useLocation()

  const apolloClient = useApolloClient()

  const { access } = useCurrentSession()
  const { approve, disabled } = useBulkApproveRejectFlows()

  const materializedFlowId = taskable?.materializedApprovalFlow?.id

  const actionData = materializedFlowId
    ? {
        accessId: access.id,
        materializedFlowId,
        reason: state?.additionalComments,
      }
    : undefined

  const handleSuccessApproveReject = useCallback(() => {
    apolloClient.writeFragment({
      id: `TasksInboxTask:${taskId}`,
      fragment: InboxTaskListItemCompletionFragmentDoc,
      fragmentName: 'InboxTaskListItemCompletion',
      data: {
        __typename: 'TasksInboxTask',
        id: taskId,
        completedAt: new Date().toISOString(),
        status: 'done',
      },
    })

    apolloClient.refetchQueries({
      include: [TaskActivityDocument, ApprovalsMaterializedApprovalFlowHistoryDocument],
    })
  }, [apolloClient, taskId])

  const handleApprove = async () => {
    if (!actionData) return

    await approve(actionData, handleSuccessApproveReject)

    return true
  }

  const handleReject = async () => {
    navigate({
      search,
      hash: encodeActivity('core.rejectApprovalFlow', {
        taskId: `${taskId}`,
        materializedFlowId: `${materializedFlowId}`,
      }),
    })
  }

  if (!materializedFlowId) {
    return null
  }

  const approvable = taskable?.materializedApprovalFlow?.approvable
  const category = approvable?.__typename
  const isTimeoffLeave = category === 'TimeoffLeave'

  // Only TimeoffLeave has an approval suggestion for now.
  const approvalSuggestion =
    isTimeoffLeave && 'approvalSuggestion' in approvable
      ? (approvable.approvalSuggestion ?? undefined)
      : undefined

  return (
    <TaskFooter
      taskId={taskId}
      taskKind={taskKind ?? undefined}
      onClickPrimaryAction={handleApprove}
      onClickSecondaryAction={handleReject}
      actionsDisabled={disabled}
      approvableTypename={category}
      approvalSuggestion={approvalSuggestion}
    />
  )
}

export const ApprovalTaskDefinition: TaskDefinition<'ApprovalsMaterializedApprovalStep', State> = {
  taskableTypename: 'ApprovalsMaterializedApprovalStep',
  initialState: {},
  component: ApprovalTaskContent,
  footer: ApprovalTaskFooter,
  getTitle: ({ info, affecteeName }) => {
    const approvable = info?.materializedApprovalFlow?.approvable

    const approvableTypename = approvable?.__typename

    if (!affecteeName) {
      return ''
    }

    if (approvableTypename) {
      const resourceTitleFunction = RESOURCE_TITLES[approvableTypename]

      let resourceText = ''
      const resource = resourceTitleFunction(approvable)

      if (
        typeof resource !== 'string' &&
        approvableTypename === 'BankingCard' &&
        approvable.bankCardType
      ) {
        resourceText = resource(approvable.bankCardType)
      }

      if (typeof resource === 'string') {
        resourceText = resource
      }
      return i18n.t('common.approvals.title', {
        affectee: affecteeName,
        resource: resourceText,
      })
    } else {
      const resourceType = info.materializedApprovalFlow?.resourceType
      if (!resourceType) return ''
      return i18n.t('common.approvals.title', {
        affectee: affecteeName,
        resource: getResourceTextFromResourceType(resourceType),
      })
    }
  },
  getUrl: ({ info, navigation }) => {
    const approvable = info.materializedApprovalFlow?.approvable

    if (!approvable) return null

    if (approvable.__typename === 'ContractsContractVersionRequest') {
      if (!approvable.employeeName?.id || !approvable.contractVersionRequestId) return null

      return navigation.employees.id.contractVersions.approval_request.fullPath({
        id: approvable.employeeName.id,
        contractVersionId: approvable.contractVersionRequestId,
      })
    }

    if (approvable.__typename === 'ContractsContractVersion') {
      if (!approvable.employee?.id || !approvable.contractVersionId) return null

      return navigation.employees.id.contractVersions.contractVersionId.fullPath({
        id: approvable.employee.id,
        contractVersionId: approvable.contractVersionId,
      })
    }

    if (approvable.__typename === 'ExpensesExpensable') {
      if (!approvable.expensableGroup?.id) return null

      return navigation.expenses.groups.id.fullPath({
        groupId: approvable.expensableGroup.id,
      })
    }

    return null
  },
  getMetadata: ({ info }) => {
    const approvable = info.materializedApprovalFlow?.approvable

    if (!approvable) return []

    switch (approvable.__typename) {
      case 'TimeoffLeave':
        return getTimeoffLeaveMetadata(approvable)
      case 'AtsRequisition':
        return getAtsRequisitionMetadata(approvable)
      case 'ProcurementPurchaseRequest':
        return getProcurementPurchaseRequestMetadata(approvable)
      case 'ExpensesExpensable':
        return getExpensesExpensableMetadata(approvable)
      case 'BankingCard':
        return getBankingCardMetadata(approvable)
      case 'TrainingsTrainingRequest':
        return getTrainingsTrainingRequestMetadata(approvable)
      case 'ContractsContractVersion':
        return getContractsContractVersionMetadata(approvable)
      case 'ContractsContractVersionRequest':
        return getContractsContractVersionRequestMetadata(approvable)
      case 'AttendanceOvertimeRequest':
        return getAttendanceOvertimeRequestMetadata(approvable)
      case 'ShiftManagementSwapShift':
        return getShiftManagementSwapShiftMetadata(approvable)
      case 'AttendanceEditTimesheetRequest':
        return getAttendanceEditTimesheetRequestMetadata(approvable)
      default:
        return []
    }
  },
}
